import {
  useEffect,
  useCallback,
  useState,
  PropsWithChildren,
  createContext,
} from 'react'
import { Amplify, Auth, Hub } from 'aws-amplify'
import { Browser } from '@capacitor/browser'
import { Capacitor } from '@capacitor/core'
import { App as CapApp } from '@capacitor/app'

const getAuthStateFromUrl = (url: string) => {
  const urlSearchParams = new URLSearchParams(
    url.replace(/^[^?]*\?/g, ''),
  )

  const code = urlSearchParams.get('code')
  const state = urlSearchParams.get('state')
  const error = urlSearchParams.get('error')

  return { code, state, error }
}

/**
   * Handle the redirect from the Cognito hosted UI
   * @see https://auth0.com/docs/quickstart/native/ionic-react/01-login
   * @see https://forum.ionicframework.com/t/redirect-back-to-app-after-oauth2-oidc-login/201056
   * @see https://forum.ionicframework.com/t/implementing-in-app-oauth-flows-with-ionic-and-capacitor-browser/214425/1
  
   * @param url Redirect URL from Cognito with `code` and `state` query params
   */
const handleRedirectCallback = (url: string) => {
  const { code, state, error } = getAuthStateFromUrl(url)

  if (error) {
    return
  }

  const queryParams = `code=${code}&state=${state}`
  if (Capacitor.getPlatform() === 'android') {
    window.location.replace(
      `http://localhost/?${queryParams}`,
    )
    return
  }

  if (Capacitor.getPlatform() === 'ios') {
    window.history.replaceState(
      null,
      'Lahilo',
      `com.aprovan.lahilo://localhost/?${queryParams}`,
    )
    window.location.reload()
  }
}

Amplify.configure({
  Auth: {
    region: process.env.REACT_APP_AWS_REGION,
    userPoolId: process.env.REACT_APP_USER_POOL_ID,
    userPoolWebClientId:
      process.env.REACT_APP_COGNITO_CLIENT_ID,
    oauth: {
      domain: `${process.env.REACT_APP_COGNITO_DOMAIN_NAME}.auth.${process.env.REACT_APP_AWS_REGION}.amazoncognito.com`,
      scope: ['openid', 'aws.cognito.signin.user.admin'],
      redirectSignIn: process.env.REACT_APP_UI_URL,
      redirectSignOut: process.env.REACT_APP_UI_URL,
      responseType: 'code',
      urlOpener: async (url: string): Promise<void> =>
        Browser.open({
          url,
          windowName: Capacitor.isNativePlatform()
            ? '_blank'
            : '_self',
        }),
    },
  },
})

export type User = {
  sub?: string
  iss?: string
  version?: string
  clientId?: string
  originJti?: string
  tokenUse?: string
  scope?: string
  authTime?: string
  exp?: string
  iat?: string
  jti?: string
  username: string
  orgId?: string
  accountType: 'ADMIN' | 'USER'
}

type Payload = {
  ['custom:account_type']: 'ADMIN' | 'USER'
  token: string
  ['family_name']: string
  sub: string
  iss: string
  version: string
  ['client_id']: string
  ['origin_jti']: string
  ['token_use']: string
  scope: string
  ['auth_time']: string
  exp: string
  iat: string
  jti: string
  username: string
}

type AuthState = {
  isLoading: boolean
  error?: string
  user?: User
  token?: string
}

type AuthActions = {
  login: () => void
  logout: () => void
}

const AuthContext = createContext<AuthState>({
  isLoading: false,
})
const AuthActionsContext = createContext<AuthActions>({
  login: () => {},
  logout: () => {},
})

const AuthProvider: React.FC<PropsWithChildren> = ({
  children,
}) => {
  const [context, setContext] = useState<AuthState>({
    isLoading: false,
  })
  const { token } = context ?? {}

  const loginFailure = useCallback((error: string) => {
    setContext({
      error,
      isLoading: false,
    })
  }, [])

  const loginSuccess = useCallback((payload: Payload) => {
    const accountType =
      payload['custom:account_type'] || 'USER'

    const token = payload['token']

    // Basic params
    const {
      sub,
      iss,
      version,
      scope,
      exp,
      iat,
      jti,
      username,
      family_name: orgId,
      client_id: clientId,
      origin_jti: originJti,
      token_use: tokenUse,
      auth_time: authTime,
    } = payload

    setContext({
      isLoading: false,
      token,
      user: {
        sub,
        iss,
        version,
        clientId,
        originJti,
        tokenUse,
        scope,
        authTime,
        exp,
        iat,
        jti,
        username,
        orgId,
        accountType,
      },
    })
  }, [])

  const login = useCallback(() => {
    setContext((context: AuthState) => ({
      ...context,
      isLoading: true,
      error: undefined,
    }))
    window.localStorage.clear()
    Auth.federatedSignIn()
  }, [setContext])

  const logout = useCallback(() => {
    setContext({ isLoading: true })
    window.localStorage.clear()

    setTimeout(() => window.location.reload(), 3000)
    Browser.open({
      url: `https://${process.env.REACT_APP_COGNITO_DOMAIN_NAME}.auth.${process.env.REACT_APP_AWS_REGION}.amazoncognito.com/logout?client_id=${process.env.REACT_APP_COGNITO_CLIENT_ID}&logout_uri=${process.env.REACT_APP_UI_URL}`,
      windowName: Capacitor.isNativePlatform()
        ? '_blank'
        : '_self',
    })
  }, [setContext])

  useEffect(() => {
    // From https://docs.amplify.aws/lib/auth/social/q/platform/js/#setup-frontend
    Hub.listen(
      'auth',
      async ({ payload: { event, data } }) => {
        try {
          switch (event) {
            case 'cognitoHostedUI':
              const token =
                data.signInUserSession.accessToken.getJwtToken()
              const payload =
                data.signInUserSession.accessToken.payload

              loginSuccess({ token, ...payload })
              break
            case 'tokenRefresh':
              break
            case 'signOut':
              break
            case 'signedOut':
              break
            case 'oAuthSignOut':
              break
            case 'signIn_failure':
              break
            case 'cognitoHostedUI_failure':
              loginFailure(event)
              throw Error('Failed to sign in')
            case 'tokenRefresh_failure':
              loginFailure(event)
              throw Error('Failed to refresh')
            default:
              break
          }
        } catch (error) {
          console.error(data)
        }
      },
    )

    const checkSession = async () => {
      try {
        const userInfo =
          await Auth.currentAuthenticatedUser()

        if (!userInfo) {
          // login()
          return
        }

        const token =
          userInfo.signInUserSession.accessToken.getJwtToken()
        const payload =
          userInfo.signInUserSession.accessToken.payload

        if (!token || !payload || !payload['username'])
          return

        loginSuccess({ token, ...payload })
      } catch (error) {
        console.error(error)
      }
    }
    checkSession()
  }, [login, loginFailure, loginSuccess])

  const loadExistingUser = useCallback(() => {
    ;(async () => {
      const session = (
        await Auth.currentSession()
      )?.getAccessToken()
      const sessionToken = session.getJwtToken()

      if (sessionToken) {
        const payload = session.payload
        loginSuccess({
          token: sessionToken,
          ...payload,
        } as Payload)
        return
      }
    })().catch(() => {})
  }, [loginSuccess])

  useEffect(() => {
    loadExistingUser()
  }, [token, loginSuccess, login, loadExistingUser])

  const { code } = getAuthStateFromUrl(window.location.href)
  useEffect(() => {
    if (!code) return
    setContext({ isLoading: true })
    loadExistingUser()
  }, [setContext, loadExistingUser, code])

  useEffect(() => {
    CapApp.addListener('appUrlOpen', async ({ url }) => {
      if (
        url.includes('state') &&
        (url.includes('code') || url.includes('error'))
      ) {
        setContext({ isLoading: true })
        handleRedirectCallback(url)
      }

      return Browser.close()
    })
  }, [])

  return (
    <AuthContext.Provider value={context}>
      <AuthActionsContext.Provider
        value={{ login, logout }}
      >
        {children}
      </AuthActionsContext.Provider>
    </AuthContext.Provider>
  )
}

export { AuthProvider, AuthContext, AuthActionsContext }
export default AuthProvider
